import { Fragment } from 'react';
import PropTypes from 'prop-types';
import { FormControl, FormHelperText, IconButton, InputBase, InputLabel } from '@mui/material';

import { roundedComponentVariants } from 'constants/components';
import { HidePasswordIcon, ShowPasswordIcon } from 'assets/icons';

/**
 * Filters out non-typographic symbols except spaces
 * Allows: letters, numbers, typographic symbols (punctuation), and spaces
 * Removes: control characters, non-printable characters, etc.
 */
const filterNonTypographicSymbols = (value) => {
  return value.replace(/[^\p{L}\p{N}\p{P}\p{S} ]/gu, '');
};

const forwardedPropDefaults = {
  autoComplete: 'off',
  autoFocus: false,
  readOnly: false,
  startAdornment: null,
  disabled: false,
};

const styles = {
  control: ({ palette }, variant) => ({
    width: '100%',
    display: 'flex',
    gap: 1,

    '.MuiInputLabel-shrink': {
      color: `${palette.text.primary} !important`,
      fontFamily: 'Poppins !important',
    },

    '.MuiInputBase-root': {
      fontFamily: 'Poppins !important',
      borderRadius: `${variant === roundedComponentVariants.rounded ? 100 : 10}px !important`,
      border: `1px solid ${palette.colors.lightGrey} !important`,
      backgroundColor: palette.common.white,

      '&.Mui-error': {
        borderColor: `${palette.error.main} !important`,
      },

      '.MuiInputBase-input': {
        paddingBlock: 2,
        paddingInline: 3,
      },
    },
  }),
  label: ({ typography }) => ({
    ...typography['subtitle1-bold'],
    position: 'static',
    transform: 'unset',
    textOverflow: 'unset',
    overflow: 'visible',
    whiteSpace: 'normal',
  }),
  input: ({ palette, typography }, multiline, autoExpand) => ({
    ...typography.body2,
    minHeight: 56,
    height: multiline && autoExpand ? 'auto' : 56,
    overflow: multiline && autoExpand ? 'visible' : 'hidden',
    '&.Mui-readOnly': {
      color: palette.colors.darkGrey,
    },
    '.MuiInputBase-input::placeholder': {
      color: palette.colors.darkGrey,
      opacity: 1,
    },
  }),
  helperText: {
    position: 'absolute',
    top: '100%',
    left: 0,
    marginTop: 0.5,
  },
  iconButton: {
    position: 'absolute',
    top: 0,
    right: 0,
    padding: 0,
  },
};

export const Input = ({
  id,
  variant = roundedComponentVariants.rounded,
  innerRef = null,
  label,
  onChange = () => {},
  disableInputFilter = false,
  withHiddenLabel = false,
  required = false,
  customStyles = {},
  inputButtonProps = null,
  error = false,
  inputProps = {},
  isMultilineError = false,
  multiline,
  autoExpand = false,
  placeholder = '',
  ...props
}) => {
  const inputId = `${id}-input`;
  const helperTextId = `${id}-input-helper-text`;
  const inputButtonId = `${id}-input-button`;

  return (
    <FormControl
      error={!!error}
      variant="standard"
      sx={[(theme) => styles.control(theme, variant), customStyles.control]}
    >
      {!withHiddenLabel && (
        <InputLabel disableAnimation shrink htmlFor={inputId} sx={[styles.label, customStyles.label]}>
          {`${required ? '*' : ''}${label}`}
        </InputLabel>
      )}

      <InputBase
        id={inputId}
        ref={innerRef}
        multiline={multiline}
        onChange={(event) => {
          const { value } = event.target;
          const sanitizedValue = disableInputFilter ? value : filterNonTypographicSymbols(value);

          onChange(sanitizedValue.trimStart());
        }}
        sx={[(theme) => styles.input(theme, multiline, autoExpand), customStyles.input]}
        inputProps={{
          ...inputProps,
          'aria-describedby': helperTextId,
          'aria-label': withHiddenLabel ? label : undefined,
        }}
        placeholder={placeholder}
        {...forwardedPropDefaults}
        {...props}
      />

      {error && (
        <FormHelperText id={helperTextId} sx={[styles.helperText, customStyles.helperText]}>
          {isMultilineError ? (
            <>
              {error.message.split('\n').map((message) => (
                <Fragment key={message}>
                  {message}
                  <br />
                </Fragment>
              ))}
            </>
          ) : (
            error.message
          )}
        </FormHelperText>
      )}

      {inputButtonProps && (
        <IconButton id={inputButtonId} onClick={inputButtonProps.onClick} sx={styles.iconButton}>
          {inputButtonProps.showPassword ? <ShowPasswordIcon /> : <HidePasswordIcon />}
        </IconButton>
      )}
    </FormControl>
  );
};

Input.propTypes = {
  id: PropTypes.string.isRequired,
  variant: PropTypes.oneOf(Object.values(roundedComponentVariants)),
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  disableInputFilter: PropTypes.bool,
  label: PropTypes.string.isRequired,
  withHiddenLabel: PropTypes.bool,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  customStyles: PropTypes.shape({
    control: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    input: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    label: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    helperText: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  }),
  inputButtonProps: PropTypes.shape({
    onClick: PropTypes.func,
    showPassword: PropTypes.bool,
  }),
  inputProps: PropTypes.shape({
    maxLength: PropTypes.number,
  }),
  autoComplete: PropTypes.string,
  autoFocus: PropTypes.bool,
  readOnly: PropTypes.bool,
  error: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      type: PropTypes.string,
      message: PropTypes.string,
      ref: PropTypes.shape({}),
    }),
  ]),
  startAdornment: PropTypes.node,
  isMultilineError: PropTypes.bool,
  autoExpand: PropTypes.bool,
};
